import type { ReactNode } from 'react';
import { useResourceKit } from '@resource-kit/core/react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '../primitives/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../primitives/Select';

/**
 * One pager for both pagination modes (#1801).
 *
 * `DataTable` used to carry two ~70-line footers that differed only in where
 * the numbers came from — server props vs the TanStack table instance — and in
 * what the left-hand caption said. Two copies of the same control is how the
 * first-page button ends up disabled in one mode and not the other. Both modes
 * now project onto this single model; the projection is the only thing that
 * differs, and it is three lines each.
 */
export interface PaginationModel {
  /** 1-based current page. */
  page: number;
  /** Total number of pages. Sources that report 0 for an empty list (a server
   *  `totalPages` of 0, `table.getPageCount()` on an empty table) are floored
   *  at 1 for display: an empty list is one empty page. The same floored value
   *  feeds the last-page target, but that is unreachable defense-in-depth
   *  rather than a live guarantee — at a raw count of 0 `canNextPage` is false,
   *  so the Last button is already disabled. */
  pageCount: number;
  pageSize: number;
  /** Page-size options offered in the rows-per-page select. */
  pageSizeOptions: readonly number[];
  onPageChange: (page: number) => void;
  /** Omit to render no rows-per-page control at all (a dead one lies). */
  onPageSizeChange?: (size: number) => void;
  canPreviousPage: boolean;
  canNextPage: boolean;
  /** Caption on the left (record count, selection count, …). */
  caption: ReactNode;
}

export function DataTablePagination({ model }: { model: PaginationModel }) {
  const { t } = useResourceKit();
  // See the `pageCount` JSDoc: an empty list is one empty page, never zero.
  const pageCount = Math.max(1, model.pageCount);
  return (
    <div
      data-testid="data-table-pagination"
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-2 sm:gap-x-4"
    >
      <div className="whitespace-nowrap text-sm text-muted-foreground">{model.caption}</div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:gap-x-6 lg:gap-x-8">
        {model.onPageSizeChange && (
          <div className="flex items-center">
            <Select
              value={String(model.pageSize)}
              onValueChange={(value) => model.onPageSizeChange?.(Number(value))}
            >
              <SelectTrigger
                className="h-8 w-auto min-w-[70px]"
                aria-label={t('rk.table.rowsPerPage')}
              >
                <SelectValue placeholder={t('rk.table.perPageOption', { count: model.pageSize })} />
              </SelectTrigger>
              <SelectContent positionerProps={{ side: 'top' }}>
                {model.pageSizeOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {t('rk.table.perPageOption', { count: option })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center justify-center whitespace-nowrap text-sm font-medium">
          {t('rk.table.pageOf', { page: model.page, total: pageCount })}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => model.onPageChange(1)}
            disabled={!model.canPreviousPage}
          >
            <span className="sr-only">{t('rk.table.goToFirstPage')}</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => model.onPageChange(model.page - 1)}
            disabled={!model.canPreviousPage}
          >
            <span className="sr-only">{t('rk.table.goToPrevPage')}</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => model.onPageChange(model.page + 1)}
            disabled={!model.canNextPage}
          >
            <span className="sr-only">{t('rk.table.goToNextPage')}</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => model.onPageChange(pageCount)}
            disabled={!model.canNextPage}
          >
            <span className="sr-only">{t('rk.table.goToLastPage')}</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
